import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import DataBase from "../../dataBase/liquidGuides";
import Guide from "../../Models/Guide";
import {
  diffPlaces,
  diffEvents,
  diffItinerary,
  diffGuideInfo,
  diffGuideSettings,
  diffCatchall,
  summarizeDiff,
  stableEventKey,
  applyPlacesMerge,
  applyEventsMerge,
  applyItineraryMerge,
  applyGuideInfoMerge,
  applyGuideSettingsMerge,
} from "../../Common/GuideDiff";
import PlaceDiffCard from "./PlaceDiffCard";
import EventDiffCard from "./EventDiffCard";
import GuideInfoDiffCard from "./GuideInfoDiffCard";
import "./Modal.css";

// Phase 3 of the save-conflict feature: an interactive per-field merge on top
// of Phase 2's read-only diff. Fetched lazily — only when this modal is
// actually opened, via the bare DataBase.readGuideData (no times_read bump /
// analytics / user lookups, unlike readGuideTransaction) — not on every
// conflict.
//
// onApplyMerge(mergedPlacesList, mergedEvents, mergedDocument, mergedGuideInfo,
// mergedGuideSettings) is called once the user has made every required
// choice and clicked "Apply Merge & Save" — the caller (HomeHeader) is
// responsible for writing these onto window.guide_obj and re-running the
// actual save (same _saveGuideWithExpectedMod path "Write Anyway" already
// uses), since this modal has no DataBase/redux coupling by design (matches
// GuideDiff.js's own stated decoupling).
export default function SaveConflictDiffModal({ show, onHide, serverGuideInfo, onApplyMerge }) {
  const [status, setStatus] = useState("idle"); // idle | loading | error | ready
  const [placesDiff, setPlacesDiff] = useState(null);
  const [eventsDiff, setEventsDiff] = useState(null);
  const [itineraryDiff, setItineraryDiff] = useState(null);
  const [guideInfoDiff, setGuideInfoDiff] = useState(null);
  const [guideSettingsDiff, setGuideSettingsDiff] = useState(null);
  const [catchallDiff, setCatchallDiff] = useState(null);
  const [serverPlacesList, setServerPlacesList] = useState(null);
  const [serverEvents, setServerEvents] = useState(null);
  const [expanded, setExpanded] = useState({ places: false, events: false, itinerary: false, guideInfo: false, guideSettings: false });

  // Selections: includeSelections keyed by Place.ID / stableEventKey(event)
  // for added/removed items (true/false; undefined == default-included).
  // fieldSelections keyed the same way -> { [field]: "mine"|"theirs" }.
  const [placeIncludeSelections, setPlaceIncludeSelections] = useState({});
  const [placeFieldSelections, setPlaceFieldSelections] = useState({});
  const [eventIncludeSelections, setEventIncludeSelections] = useState({});
  const [eventFieldSelections, setEventFieldSelections] = useState({});
  // Unlike a changed place/event FIELD (genuinely ambiguous which side should
  // win, so those start unset), the itinerary/guide-settings are single
  // binary choices — default to "mine", matching the same "keep what you've
  // got" reasoning already applied to added/removed items' include
  // checkboxes (default included). User can still flip to "theirs".
  const [itineraryChoice, setItineraryChoice] = useState("mine");
  const [guideSettingsChoice, setGuideSettingsChoice] = useState("mine");
  // Guide Info fields (Name, Description, Cover Photo, etc.) are genuinely
  // independent edits like a changed place/event field — no default.
  const [guideInfoFieldSelections, setGuideInfoFieldSelections] = useState({});

  useEffect(() => {
    if (!show || status !== "idle") return;
    setStatus("loading");
    DataBase.readGuideData(serverGuideInfo?.guide_id, (res) => {
      if (res.pass !== true) {
        setStatus("error");
        return;
      }
      try {
        const serverGuideJson = JSON.parse(res.res.data);
        const serverGuide = new Guide(res.guide_id);
        serverGuide.decodeGuideFb(serverGuideJson, serverGuideInfo);
        setPlacesDiff(diffPlaces(window.guide_obj.places.placesList, serverGuide.places.placesList));
        setEventsDiff(diffEvents(window.guide_obj.schedule.events, serverGuide.schedule.events));
        setItineraryDiff(diffItinerary(window.guide_obj.getDocumentHTML(), serverGuide.getDocumentHTML()));
        // serverGuideInfo is the guide_info doc already fetched for the
        // conflict dialog (infoDoc.data()) — no extra fetch needed here,
        // unlike places/events/itinerary which live in the separate
        // guide_data doc this effect just fetched.
        setGuideInfoDiff(diffGuideInfo(window.guide_obj.attributes, serverGuideInfo));
        setGuideSettingsDiff(diffGuideSettings(window.guide_obj.guide_settings, serverGuide.guide_settings));
        // Safety net — see diffCatchall's own comment. localSnapshot strips
        // methods via the JSON round-trip, matching what actually gets
        // serialized on save. Their side goes through the same decode
        // (serverGuide, not the raw stored JSON) so load-time normalization
        // isn't mistaken for a difference.
        const localSnapshot = JSON.parse(JSON.stringify(window.guide_obj));
        setCatchallDiff(diffCatchall(localSnapshot, JSON.parse(JSON.stringify(serverGuide))));
        setServerPlacesList(serverGuide.places.placesList);
        setServerEvents(serverGuide.schedule.events);
        setStatus("ready");
      } catch (e) {
        console.log("SaveConflictDiffModal decode error", e.message);
        setStatus("error");
      }
    });
    // Re-fetch fresh each time the modal is reopened for a new conflict —
    // guarded by `status !== "idle"` above and reset on hide (see resetOnHide).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const resetOnHide = () => {
    setStatus("idle");
    setPlacesDiff(null);
    setEventsDiff(null);
    setItineraryDiff(null);
    setGuideInfoDiff(null);
    setGuideSettingsDiff(null);
    setCatchallDiff(null);
    setServerPlacesList(null);
    setServerEvents(null);
    setExpanded({ places: false, events: false, itinerary: false, guideInfo: false, guideSettings: false });
    setPlaceIncludeSelections({});
    setPlaceFieldSelections({});
    setEventIncludeSelections({});
    setEventFieldSelections({});
    setItineraryChoice("mine");
    setGuideSettingsChoice("mine");
    setGuideInfoFieldSelections({});
    onHide();
  };

  const eventEntries = eventsDiff
    ? Object.entries(eventsDiff).flatMap(([itinerary, d]) => [
        ...d.added.map((event) => ({ kind: "added", itinerary, event })),
        ...d.removed.map((event) => ({ kind: "removed", itinerary, event })),
        ...d.changed.map((c) => ({ kind: "changed", itinerary, ...c })),
      ])
    : [];

  // "Apply Merge & Save" stays disabled until every changed field across
  // every changed place/event/guide-info has an explicit choice, and the
  // itinerary/guide-settings choice is made if either differs — deliberately
  // no default for genuinely ambiguous per-field picks, since silently
  // picking either side for the user is exactly the data-loss risk this
  // feature exists to prevent.
  const allChangedFieldsChosen = () => {
    if (placesDiff) {
      for (const c of placesDiff.changed) {
        const choices = placeFieldSelections[c.local.ID] || {};
        if (c.changedFields.some((f) => !choices[f])) return false;
      }
    }
    if (eventsDiff) {
      for (const entry of eventEntries) {
        if (entry.kind !== "changed") continue;
        const key = stableEventKey(entry.local);
        const choices = eventFieldSelections[key] || {};
        if (entry.changedFields.some((f) => !choices[f])) return false;
      }
    }
    if (guideInfoDiff?.changed && guideInfoDiff.changedFields.some((f) => !guideInfoFieldSelections[f])) return false;
    if (itineraryDiff?.changed && !itineraryChoice) return false;
    if (guideSettingsDiff?.changed && !guideSettingsChoice) return false;
    return true;
  };
  const canApply = status === "ready" && allChangedFieldsChosen();

  const _setPlaceField = (placeID, field, choice) =>
    setPlaceFieldSelections((prev) => ({ ...prev, [placeID]: { ...prev[placeID], [field]: choice } }));
  const _setPlaceInclude = (placeID, included) =>
    setPlaceIncludeSelections((prev) => ({ ...prev, [placeID]: included }));
  const _setEventField = (key, field, choice) =>
    setEventFieldSelections((prev) => ({ ...prev, [key]: { ...prev[key], [field]: choice } }));
  const _setEventInclude = (key, included) =>
    setEventIncludeSelections((prev) => ({ ...prev, [key]: included }));
  const _setGuideInfoField = (field, choice) =>
    setGuideInfoFieldSelections((prev) => ({ ...prev, [field]: choice }));

  const _applyMerge = () => {
    const mergedPlaces = applyPlacesMerge(placesDiff, placeIncludeSelections, placeFieldSelections, serverPlacesList);
    const mergedEvents = applyEventsMerge(eventsDiff, eventIncludeSelections, eventFieldSelections, serverEvents);
    const mergedDocument = itineraryDiff?.changed
      ? applyItineraryMerge(itineraryChoice, itineraryDiff.local, itineraryDiff.server)
      : itineraryDiff?.server ?? window.guide_obj.getDocumentHTML();
    const mergedGuideInfo = applyGuideInfoMerge(guideInfoDiff, guideInfoFieldSelections);
    const mergedGuideSettings = guideSettingsDiff?.changed
      ? applyGuideSettingsMerge(guideSettingsChoice, guideSettingsDiff.local, guideSettingsDiff.server)
      : guideSettingsDiff?.server ?? window.guide_obj.guide_settings;
    onApplyMerge(mergedPlaces, mergedEvents, mergedDocument, mergedGuideInfo, mergedGuideSettings);
    resetOnHide();
  };

  return (
    <Modal show={show} onHide={resetOnHide} centered scrollable dialogClassName="am-dialog">
      <Modal.Header closeButton>
        <h2 className="am-title">What changed</h2>
      </Modal.Header>
      <Modal.Body>
        {status === "loading" && <p>Loading their version…</p>}
        {status === "error" && <p>Could not load their version to compare. Try again shortly.</p>}
        {status === "ready" && (
          <>
            <p>{summarizeDiff(placesDiff, eventsDiff)}</p>
            <p className="tai-row-key">
              Pick, field by field, whether to keep your version or theirs. Nothing is saved until you click
              "Apply Merge &amp; Save" below.
            </p>
            {catchallDiff?.changed && (
              <p className="tai-catchall-warning">
                ⚠ Something else about this guide also differs (e.g. Groups, Categories, or another field not shown
                above in detail) — not individually reviewable here. "Write Anyway" from the previous screen is
                safer if you need that other content preserved exactly.
              </p>
            )}

            <div className="tai-divider" />
            <div className="tai-row-item" onClick={() => setExpanded((p) => ({ ...p, places: !p.places }))}>
              <span className="tai-row-key">
                Places ({placesDiff.added.length + placesDiff.removed.length + placesDiff.changed.length} changed)
              </span>
              <span className="tai-row-chevron">{expanded.places ? "▲" : "▼"}</span>
            </div>
            {expanded.places && (
              <>
                {placesDiff.added.map((place) => (
                  <PlaceDiffCard
                    key={`added-${place.ID}`}
                    place={place}
                    kind="added"
                    included={placeIncludeSelections[place.ID]}
                    onToggleInclude={(included) => _setPlaceInclude(place.ID, included)}
                  />
                ))}
                {placesDiff.removed.map((place) => (
                  <PlaceDiffCard
                    key={`removed-${place.ID}`}
                    place={place}
                    kind="removed"
                    included={placeIncludeSelections[place.ID]}
                    onToggleInclude={(included) => _setPlaceInclude(place.ID, included)}
                  />
                ))}
                {placesDiff.changed.map((c) => (
                  <PlaceDiffCard
                    key={`changed-${c.server.ID}`}
                    place={c.server}
                    changedFrom={c.local}
                    changedFields={c.changedFields}
                    kind="changed"
                    fieldChoices={placeFieldSelections[c.local.ID]}
                    onFieldChoice={(field, choice) => _setPlaceField(c.local.ID, field, choice)}
                  />
                ))}
                {placesDiff.added.length + placesDiff.removed.length + placesDiff.changed.length === 0 && (
                  <p>No place differences.</p>
                )}
              </>
            )}

            <div className="tai-divider" />
            <div className="tai-row-item" onClick={() => setExpanded((p) => ({ ...p, events: !p.events }))}>
              <span className="tai-row-key">Events ({eventEntries.length} changed)</span>
              <span className="tai-row-chevron">{expanded.events ? "▲" : "▼"}</span>
            </div>
            {expanded.events && (
              <>
                {eventEntries.map((entry, i) => {
                  if (entry.kind === "changed") {
                    const key = stableEventKey(entry.local);
                    return (
                      <EventDiffCard
                        key={`changed-${i}`}
                        event={entry.server}
                        changedFrom={entry.local}
                        changedFields={entry.changedFields}
                        itinerary={entry.itinerary}
                        kind="changed"
                        fieldChoices={eventFieldSelections[key]}
                        onFieldChoice={(field, choice) => _setEventField(key, field, choice)}
                      />
                    );
                  }
                  const key = stableEventKey(entry.event);
                  return (
                    <EventDiffCard
                      key={`${entry.kind}-${i}`}
                      event={entry.event}
                      itinerary={entry.itinerary}
                      kind={entry.kind}
                      included={eventIncludeSelections[key]}
                      onToggleInclude={(included) => _setEventInclude(key, included)}
                    />
                  );
                })}
                {eventEntries.length === 0 && <p>No event differences.</p>}
              </>
            )}

            <div className="tai-divider" />
            <div className="tai-row-item" onClick={() => setExpanded((p) => ({ ...p, guideInfo: !p.guideInfo }))}>
              <span className="tai-row-key">
                Guide Info {guideInfoDiff?.changed ? `(${guideInfoDiff.changedFields.length} changed)` : "(no differences)"}
              </span>
              <span className="tai-row-chevron">{expanded.guideInfo ? "▲" : "▼"}</span>
            </div>
            {expanded.guideInfo && guideInfoDiff?.changed && (
              <GuideInfoDiffCard
                guideInfoDiff={guideInfoDiff}
                fieldChoices={guideInfoFieldSelections}
                onFieldChoice={_setGuideInfoField}
              />
            )}

            <div className="tai-divider" />
            <div className="tai-row-item" onClick={() => setExpanded((p) => ({ ...p, itinerary: !p.itinerary }))}>
              <span className="tai-row-key">Itinerary {itineraryDiff?.changed ? "(changed)" : "(no differences)"}</span>
              <span className="tai-row-chevron">{expanded.itinerary ? "▲" : "▼"}</span>
            </div>
            {expanded.itinerary && itineraryDiff?.changed && (
              <div className="tai-dest-card">
                <p className="tai-row-key">
                  The itinerary can't be merged field-by-field — pick one version in full.
                </p>
                <label style={{ display: "block", marginBottom: 8, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="itinerary-choice"
                    checked={itineraryChoice === "mine"}
                    onChange={() => setItineraryChoice("mine")}
                  />{" "}
                  Keep my itinerary
                </label>
                <label style={{ display: "block", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="itinerary-choice"
                    checked={itineraryChoice === "theirs"}
                    onChange={() => setItineraryChoice("theirs")}
                  />{" "}
                  Keep their itinerary
                </label>
              </div>
            )}

            <div className="tai-divider" />
            <div className="tai-row-item" onClick={() => setExpanded((p) => ({ ...p, guideSettings: !p.guideSettings }))}>
              <span className="tai-row-key">
                Guide Settings {guideSettingsDiff?.changed ? "(changed)" : "(no differences)"}
              </span>
              <span className="tai-row-chevron">{expanded.guideSettings ? "▲" : "▼"}</span>
            </div>
            {expanded.guideSettings && guideSettingsDiff?.changed && (
              <div className="tai-dest-card">
                <p className="tai-row-key">
                  Map/list display preferences can't be merged field-by-field — pick one version in full.
                </p>
                <label style={{ display: "block", marginBottom: 8, cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="guide-settings-choice"
                    checked={guideSettingsChoice === "mine"}
                    onChange={() => setGuideSettingsChoice("mine")}
                  />{" "}
                  Keep my settings
                </label>
                <label style={{ display: "block", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="guide-settings-choice"
                    checked={guideSettingsChoice === "theirs"}
                    onChange={() => setGuideSettingsChoice("theirs")}
                  />{" "}
                  Keep their settings
                </label>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      {status === "ready" && (
        <Modal.Footer>
          <Button variant="primary" disabled={!canApply} onClick={_applyMerge}>
            Apply Merge &amp; Save
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
